import { computed, onMounted, onUnmounted, ref } from 'vue'
import { executeShiftCopy, previewShiftCopy } from '@/api/schedule'
import type { CopyError, ShiftCopyExecuteResponse, ShiftCopyPreviewResponse, ShiftCopyRequest } from '@/api/schedule'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useToast } from 'vue-toast-notification'

type CopyResult = NonNullable<ShiftCopyExecuteResponse['result']>

interface PendingOperation {
    payload: ShiftCopyRequest
    onSuccess?: () => void | Promise<void>
    confirmOnPreview: boolean
    // Текст toast успеха по фактическому result; без него — прежний «Вставлено N смен».
    successMessage?: (result: CopyResult, payload: ShiftCopyRequest) => string
    // После обновления графика и проверки правил (например, подсветка скопированных ячеек).
    onCompleted?: (result: CopyResult, payload: ShiftCopyRequest) => void
}

interface CopyApiErrorResponse {
    body?: {
        errors?: CopyError[]
    }
}

function getCopyErrors(error: unknown): CopyError[] {
    if (!error || typeof error !== 'object' || !('response' in error)) return []

    const response = error.response
    if (!response || typeof response !== 'object' || !('data' in response)) return []

    const data = response.data as CopyApiErrorResponse
    return Array.isArray(data?.body?.errors) ? data.body.errors : []
}

function uniqueCopyErrors(errors: CopyError[]) {
    return [...new Map(errors.map(error => [
        `${error.code}\u0000${error.message}\u0000${error.details?.employment_id ?? ''}`,
        error,
    ])).values()]
}

function withEmployeeName(error: CopyError, employeeName: string, employmentId: string): CopyError {
    return {
        ...error,
        details: {
            ...error.details,
            employment_id: error.details?.employment_id ?? employmentId,
            employee_name: error.details?.employee_name ?? employeeName,
        },
    }
}

export function pluralRu(count: number, forms: [string, string, string]) {
    const remainder100 = count % 100
    const remainder10 = count % 10
    if (remainder100 >= 11 && remainder100 <= 14) return forms[2]
    if (remainder10 === 1) return forms[0]
    if (remainder10 >= 2 && remainder10 <= 4) return forms[1]
    return forms[2]
}

export const formatShiftsCount = (count: number) => `${count} ${pluralRu(count, ['смена', 'смены', 'смен'])}`

// Текст ошибки копирования — как в ShiftCopyModal.
export function formatCopyError(error: CopyError) {
    const employeeName = error.details?.employee_name
    const message = error.message || 'Не удалось выполнить копирование смен'
    return typeof employeeName === 'string' ? `${employeeName}: ${message}` : message
}

export function useShiftCopy(options: { backgroundRefresh?: boolean } = {}) {
    const scheduleStore = useScheduleStore()
    const toast = useToast({ position: 'bottom-right' })
    const isLoading = ref(false)
    const modalMode = ref<'confirm' | 'errors'>('errors')
    const modalErrors = ref<CopyError[]>([])
    const preview = ref<ShiftCopyPreviewResponse['preview']>(null)
    const pendingOperation = ref<PendingOperation | null>(null)
    const requestVersion = ref(0)
    // Владелец (панель/таблица) размонтирован: отправленный запрос не отменяется, но его UI-колбэки
    // не трогают новый контекст, а preview больше не запускает execute.
    let isDetached = false

    const isModalOpen = computed(() => Boolean(pendingOperation.value && (modalMode.value === 'confirm' || modalErrors.value.length)))

    const showErrors = (errors: CopyError[]) => {
        modalMode.value = 'errors'
        const uniqueErrors = uniqueCopyErrors(errors)
        modalErrors.value = uniqueErrors.length
            ? uniqueErrors
            : [{ code: 'COPY_FAILED', message: 'Не удалось выполнить копирование смен' }]
    }

    const formatShifts = formatShiftsCount

    const refreshSchedule = async () => {
        await scheduleStore.fetchSchedule({ background: options.backgroundRefresh })
    }

    const checkCopiedShiftRules = (payload: ShiftCopyRequest) => scheduleStore.checkCopiedShiftRules(payload)

    const refreshAfterCopy = async (operation: PendingOperation) => {
        if (!isDetached) await operation.onSuccess?.()
        const warnings = await checkCopiedShiftRules(operation.payload)
        await scheduleStore.fetchSchedule({ checkRules: false, background: options.backgroundRefresh })
        return [...new Set(warnings)]
    }

    const executeFailureMessages = (result: NonNullable<ShiftCopyExecuteResponse['result']>) => (
        result.employments
            .filter(employment => !employment.success)
            .map(employment => {
                const previewEmployment = preview.value?.employments
                    .find(item => item.employment_id === employment.employment_id)
                const reason = employment.errors?.map(error => error.message).join(', ')
                return `${previewEmployment?.employee_name ?? employment.employment_id}${reason ? `: ${reason}` : ''}`
            })
    )

    const executeFailureErrors = (result: NonNullable<ShiftCopyExecuteResponse['result']>) => (
        result.employments.flatMap(employment => {
            if (employment.success) return []
            const employeeName = preview.value?.employments
                .find(item => item.employment_id === employment.employment_id)?.employee_name
            return (employment.errors ?? []).map(error => (
                employeeName ? withEmployeeName(error, employeeName, employment.employment_id) : error
            ))
        })
    )

    const close = () => {
        if (isLoading.value) return
        requestVersion.value += 1
        pendingOperation.value = null
        modalErrors.value = []
        preview.value = null
    }

    const execute = async (operation: PendingOperation, version: number) => {
        isLoading.value = true
        try {
            const response = await executeShiftCopy(operation.payload)
            if (version !== requestVersion.value) return

            const result = response.data.body
            if (!result.success || !result.result) {
                await refreshSchedule()
                if (version !== requestVersion.value) return
                showErrors([
                    ...(result.errors ?? []),
                    ...(result.result ? executeFailureErrors(result.result) : []),
                ])
                return
            }
            const shiftsCreated = result.result.shifts_created
            const failedMessages = executeFailureMessages(result.result)

            // onSuccess может сам закрыть UI владельца (панель редактора) — это штатный путь.
            const isAttachedOnSuccess = !isDetached
            const warnings = await refreshAfterCopy(operation)

            if (version === requestVersion.value) {
                toast.success(operation.successMessage
                    ? operation.successMessage(result.result, operation.payload)
                    : `Вставлено ${formatShifts(shiftsCreated)}`)
                if (failedMessages.length) toast.error(`Не удалось скопировать:<br>${failedMessages.join('<br>')}`)
                if (warnings.length) toast.error(warnings.join('<br>'))
                pendingOperation.value = null
                modalErrors.value = []
                preview.value = null
                if (isAttachedOnSuccess) operation.onCompleted?.(result.result, operation.payload)
            }
        } catch (error: unknown) {
            if (version !== requestVersion.value) return
            await refreshSchedule()
            showErrors(getCopyErrors(error))
        } finally {
            if (version === requestVersion.value) isLoading.value = false
        }
    }

    const start = async (
        payload: ShiftCopyRequest,
        onSuccess?: () => void | Promise<void>,
        options: Pick<PendingOperation, 'successMessage' | 'onCompleted'> & { confirmOnPreview?: boolean } = {},
    ) => {
        if (isLoading.value) return

        const operation: PendingOperation = {
            payload: {
                ...payload,
                employment_ids: [...payload.employment_ids],
                source_dates: [...payload.source_dates],
                target_dates: [...payload.target_dates],
            },
            onSuccess,
            confirmOnPreview: options.confirmOnPreview ?? false,
            successMessage: options.successMessage,
            onCompleted: options.onCompleted,
        }
        const version = ++requestVersion.value
        pendingOperation.value = operation
        modalMode.value = 'errors'
        modalErrors.value = []
        preview.value = null
        isLoading.value = true

        try {
            const response = await previewShiftCopy(operation.payload)
            if (version !== requestVersion.value || isDetached) return

            const result = response.data.body
            const skippedEmploymentErrors = result.preview?.employments.flatMap(item => (
                item.can_copy ? [] : item.errors.map(error => (
                    withEmployeeName(error, item.employee_name, item.employment_id)
                ))
            )) ?? []
            if (!result.can_copy || !result.preview) {
                showErrors([...result.errors, ...skippedEmploymentErrors])
                return
            }

            preview.value = result.preview
            if (operation.confirmOnPreview || preview.value.total_shifts_to_delete > 0) {
                modalMode.value = 'confirm'
                return
            }

            await execute(operation, version)
        } catch (error: unknown) {
            if (version !== requestVersion.value) return
            const errors = getCopyErrors(error)
            showErrors(errors)
        } finally {
            if (version === requestVersion.value) isLoading.value = false
        }
    }

    const confirm = async () => {
        const operation = pendingOperation.value
        if (!operation || modalMode.value !== 'confirm' || isLoading.value) return
        await execute(operation, requestVersion.value)
    }

    const onKeyDown = (event: KeyboardEvent) => {
        if (event.code !== 'Escape' || !isModalOpen.value || isLoading.value) return
        event.preventDefault()
        close()
    }

    onMounted(() => window.addEventListener('keydown', onKeyDown))
    onUnmounted(() => {
        isDetached = true
        window.removeEventListener('keydown', onKeyDown)
    })

    return {
        isLoading,
        isModalOpen,
        modalMode,
        modalErrors,
        preview,
        start,
        confirm,
        close,
    }
}
